import {Employee} from 'client';
import {recentSearchAtom} from 'components/search/GlobalSearch';
import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';

/**
 * Whose profiles you open, and how often: every profile page you land on,
 * from search or anywhere else, counts. The "Frequent" people rank by opens
 * weighted toward recent ones ("frecency"), so a big team never decides who
 * shows up; your own habits do. It lives in this browser, like recent
 * searches.
 */
type Open = {name: string; role: string | null; count: number; lastAt: number};

export const frequentOpensAtom = atomWithStorage<Record<string, Open>>('search:frequentOpens', {});

const DAY_MS = 24 * 60 * 60 * 1000;
// An open counts half as much after a week.
const HALF_LIFE_DAYS = 7;

const score = ({count, lastAt}: Open, now: number) =>
  count * 0.5 ** ((now - lastAt) / DAY_MS / HALF_LIFE_DAYS);

/** Counts a profile page you opened. */
export const recordOpenAtom = atom(
  null,
  (get, set, person: Pick<Employee, 'ldap' | 'name' | 'role'>) => {
    if (!person.ldap) return;
    const opens = get(frequentOpensAtom);
    const previous = opens[person.ldap];
    set(frequentOpensAtom, {
      ...opens,
      [person.ldap]: {
        name: person.name,
        role: person.role ?? previous?.role ?? null,
        count: (previous?.count ?? 0) + 1,
        lastAt: Date.now(),
      },
    });
  }
);

export type FrequentPerson = {person: Employee; count: number; lastAt: number};

export const rankFrequent = (opens: Record<string, Open>, now = Date.now()): FrequentPerson[] =>
  Object.entries(opens)
    .sort(([, a], [, b]) => score(b, now) - score(a, now))
    .map(([ldap, open]) => ({
      person: {name: open.name, ldap, user_id: ldap, role: open.role} as Employee,
      count: open.count,
      lastAt: open.lastAt,
    }));

/**
 * The people you open most, then anyone else you've picked from search
 * (today's box keeps those as recent searches, so the list isn't empty the
 * first time the new box opens), most recent first.
 */
export const frequentPeopleAtom = atom((get): Employee[] => {
  const opened = rankFrequent(get(frequentOpensAtom)).map(({person}) => person);
  const seen = new Set(opened.map(({ldap}) => ldap));
  const picked = get(recentSearchAtom).flatMap(({route, path, label}) =>
    route === 'people' && path && !seen.has(path) && seen.add(path)
      ? [{name: label, ldap: path, user_id: path, role: null} as Employee]
      : []
  );
  return [...opened, ...picked];
});
